<div class="ai-intake">
  <div class="ai-head">
    <div>
      <h2 mat-dialog-title class="mb-0">ASN from a document</h2>
      <span class="muted">{{ step === 'upload' ? 'Step 1 of 2 · read the document' : step === 'review' ? 'Step 2 of 2 · check and create' : 'Done' }}</span>
    </div>
    <button type="button" class="icon-btn" (click)="close()" aria-label="Close"><mat-icon>close</mat-icon></button>
  </div>

  <mat-dialog-content class="ai-body">
    <!-- 1. upload -->
    <ng-container *ngIf="step === 'upload'">
      <p class="muted">A supplier's packing list or PO (PDF, image or CSV). WizeHelp reads it and matches the lines to your
        SKUs; you check everything before the ASN is created.</p>

      <label class="field-label">Client</label>
      <ng-select [(ngModel)]="clientId" appearance="outline" placeholder="Select client" [searchable]="true" [clearable]="false"
        [loading]="isLoadingClients" [disabled]="isReading" appendTo="body" class="d-grid mb-3">
        <ng-option *ngFor="let c of clientList" [value]="c.id">{{ c.client_name || c.name || c.viewValue }}</ng-option>
      </ng-select>

      <label class="field-label">Document</label>
      <div class="file-row">
        <button mat-flat-button class="btn-outlined" type="button" (click)="fileInput.click()" [disabled]="isReading">Choose file</button>
        <span class="muted file-name">{{ file?.name || 'PDF, image or CSV, up to ' + maxFileMb + ' MB' }}</span>
        <input #fileInput type="file" class="d-none" [accept]="acceptedTypes" (change)="onFileSelected($event)" />
      </div>
      <mat-progress-bar *ngIf="isReading" mode="indeterminate" class="mt-3"></mat-progress-bar>
      <p *ngIf="isReading" class="muted mt-2">Reading the document — this can take up to a minute…</p>
    </ng-container>

    <!-- 2. review -->
    <ng-container *ngIf="step === 'review'">
      <div class="note warn" *ngFor="let w of draft?.warnings">{{ w }}</div>

      <div class="grid">
        <label class="field">
          <span class="field-label">Supplier</span>
          <ng-select [(ngModel)]="vendorId" placeholder="Select supplier" [searchable]="true" appendTo="body">
            <ng-option *ngFor="let v of vendorList" [value]="v.id">{{ v.name || v.viewValue }}</ng-option>
          </ng-select>
          <small class="muted" *ngIf="draft?.vendor_name && !vendorId">Document says "{{ draft.vendor_name }}"</small>
        </label>
        <label class="field">
          <span class="field-label">Warehouse</span>
          <ng-select [(ngModel)]="warehouseId" placeholder="Select warehouse" [searchable]="true" appendTo="body">
            <ng-option *ngFor="let w of warehouseList" [value]="w.id">{{ w.name || w.viewValue }}</ng-option>
          </ng-select>
        </label>
        <label class="field">
          <span class="field-label">Expected delivery</span>
          <input class="input" type="date" [(ngModel)]="expectedDate" />
        </label>
        <label class="field">
          <span class="field-label">Reference</span>
          <input class="input" [(ngModel)]="referenceNo" maxlength="50" />
        </label>
      </div>

      <div class="lines">
        <table>
          <thead>
            <tr>
              <th class="chk"></th>
              <th>SKU</th>
              <th>Product</th>
              <th class="num">Quantity</th>
              <th class="num">Cost</th>
              <th>Check</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let l of lines" [class.off]="!l.include">
              <td class="chk"><input type="checkbox" [(ngModel)]="l.include" [attr.aria-label]="'Include ' + l.product_sku" /></td>
              <td>{{ l.product_sku }}</td>
              <td>{{ l.product_name || l.description || '—' }}</td>
              <td class="num"><input class="input small" type="number" min="1" [(ngModel)]="l.quantity" [disabled]="!l.include" /></td>
              <td class="num"><input class="input small" type="number" min="0" step="0.01" [(ngModel)]="l.cost" [disabled]="!l.include" /></td>
              <td>
                <span class="flag" *ngFor="let f of l.flags">{{ flagLabel(f) }}</span>
                <span class="ok" *ngIf="!l.flags?.length">OK</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="note" *ngIf="unmatched.length">
        <b>{{ unmatched.length }} line{{ unmatched.length === 1 ? '' : 's' }} not matched to a SKU</b> (not added — add them on the
        ASN page after creating it):
        <span *ngFor="let u of unmatched; let last = last">{{ unmatchedLabel(u) }}{{ last ? '' : ', ' }}</span>
      </div>

      <label class="field mt-2">
        <span class="field-label">Notes</span>
        <input class="input" [(ngModel)]="notes" />
      </label>
    </ng-container>

    <!-- 3. done -->
    <ng-container *ngIf="step === 'done' && created">
      <div class="done">
        <mat-icon>check_circle</mat-icon>
        <b>{{ created.asn_no }} created</b>
        <span class="muted">It is a normal ASN now — edit, receive or cancel it on the ASN page.</span>
      </div>
    </ng-container>

    <p *ngIf="errorMessage" class="error mt-3">{{ errorMessage }}</p>
  </mat-dialog-content>

  <mat-dialog-actions class="ai-actions">
    <ng-container *ngIf="step === 'upload'">
      <button type="button" class="link" (click)="close()" [disabled]="isReading">Cancel</button>
      <button mat-flat-button class="btn-main" (click)="read()" [disabled]="!canRead">{{ isReading ? 'Reading…' : 'Read document' }}</button>
    </ng-container>
    <ng-container *ngIf="step === 'review'">
      <span class="problems muted" *ngIf="problems.length">{{ problems[0] }}</span>
      <button type="button" class="link" (click)="back()" [disabled]="isCreating">Back</button>
      <button mat-flat-button class="btn-main" (click)="create()" [disabled]="problems.length > 0 || isCreating">
        {{ isCreating ? 'Creating…' : 'Create ASN' }}
      </button>
    </ng-container>
    <ng-container *ngIf="step === 'done'">
      <button type="button" class="link" (click)="close()">Close</button>
      <button mat-flat-button class="btn-main" (click)="openAsn()">Open ASN</button>
    </ng-container>
  </mat-dialog-actions>
</div>
